import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Plus, Trash2, Save, Check, X } from "react-feather";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import Navigation from "./Navigation";
import Modal from "./Modal";
import LoadingSpinner from "./LoadingSpinner";
import MsspSignupApprovals from "./MsspSignupApprovals";
import SampleOrganizations from "./SampleOrganizations";
import InviteLinkBox from "./InviteLinkBox";
import LicenceCard from "./LicenceCard";
import EnzoicRelayClients from "./EnzoicRelayClients";
import SuperAdminFortinetPanel from "./SuperAdminFortinetPanel";
import SuperAdminKnowBe4Panel from "./SuperAdminKnowBe4Panel";
import { useRefetchFeatureFlags } from "../contexts/FeatureFlagsContext";
import { useRefetchLicenses } from "../contexts/LicenseContext";

const NOT_IN_LICENCE_HINT = "Not in this installation's licence";

const LICENSED_PRODUCTS = [
  { value: "Deals", label: "Deals" },
  { value: "Enterprise", label: "Enterprise" },
  { value: "BusinessUnits", label: "Business Units" },
  { value: "TPRM", label: "TPRM" },
  { value: "Ransomware", label: "Ransomware" },
  { value: "DarkWeb", label: "Dark Web" },
  { value: "AI", label: "AI" },
  { value: "EnterpriseDeals", label: "Enterprise Deals" },
  { value: "EnterpriseBusinessUnits", label: "Enterprise Business Units" },
  { value: "Applications", label: "Applications" },
  { value: "GRC", label: "GRC" },
];

// After a licence upload, the flags and licences are re-fetched once more this long afterwards, in
// case the immediate re-fetch reached a replica that had not yet refreshed its licence state.
export const LICENCE_LATE_REFETCH_MS = 60 * 1000;

const approvalStatusStyles = {
  Pending: "bg-yellow-100 text-yellow-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
};

// Pending orgs sort to the top so they surface for review; Rejected sink to the bottom.
const approvalSortOrder = (status) =>
  status === "Pending" ? 0 : status === "Rejected" ? 2 : 1;

const productColors = {
  Deals: "bg-blue-100 text-blue-800",
  Enterprise: "bg-green-100 text-green-800",
  BusinessUnits: "bg-yellow-100 text-yellow-800",
  TPRM: "bg-emerald-100 text-emerald-800",
  Ransomware: "bg-red-100 text-red-800",
  DarkWeb: "bg-indigo-100 text-indigo-800",
  AI: "bg-purple-100 text-purple-800",
  EnterpriseDeals: "bg-teal-100 text-teal-800",
  EnterpriseBusinessUnits: "bg-orange-100 text-orange-800",
  Applications: "bg-cyan-100 text-cyan-800",
  GRC: "bg-lime-100 text-lime-800",
};

const SuperAdmin = () => {
  const navigate = useNavigate();
  const apiCall = useApi();
  const { isSuperAdmin } = useRBAC();
  const refetchFlags = useRefetchFeatureFlags();
  const refetchLicenses = useRefetchLicenses();

  // Offline licence (GET /licence): drives the Licence card and, when a licence is required,
  // which products the grant modal offers. null until loaded or when the call fails.
  const [licenceInfo, setLicenceInfo] = useState(null);

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLicenses, setModalLicenses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addProductValue, setAddProductValue] = useState("");

  // Delete org state
  const [deleteConfirmOrg, setDeleteConfirmOrg] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const [actioningOrgId, setActioningOrgId] = useState(null);

  // Fortinet diagnostics panel: the org it is open for, or null.
  const [fortinetOrg, setFortinetOrg] = useState(null);
  // KnowBe4 diagnostics panel: the org it is open for, or null.
  const [knowBe4Org, setKnowBe4Org] = useState(null);
  // Owner invite link from an approval while email is off: { id, url }. Held only in component
  // state (it is a credential). Shown after the approval's own reload; the next reload clears it.
  const [ownerLink, setOwnerLink] = useState(null);

  const loadOrganizations = useCallback(async () => {
    setOwnerLink(null);
    const data = await apiCall("GET", "/SuperAdmin/organizations");
    setOrganizations(
      (data || []).sort(
        (a, b) =>
          approvalSortOrder(a.approvalStatus) - approvalSortOrder(b.approvalStatus) ||
          a.organizationName.localeCompare(b.organizationName)
      )
    );
  }, [apiCall]);

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate("/dashboard");
    }
  }, [isSuperAdmin, navigate]);

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        await loadOrganizations();
      } catch (err) {
        setError("Failed to load organizations");
      } finally {
        setLoading(false);
      }
    };
    if (isSuperAdmin()) {
      fetchOrganizations();
    }
  }, [isSuperAdmin, loadOrganizations]);

  // Fetch the installed licence once
  useEffect(() => {
    if (!isSuperAdmin()) return;
    let cancelled = false;
    apiCall("GET", "/licence")
      .then((data) => {
        if (!cancelled) setLicenceInfo(data || null);
      })
      .catch(() => {
        if (!cancelled) setLicenceInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, apiCall]);

  // One delayed re-fetch after an upload (see handleLicenceUploaded); cleared on unmount.
  const lateRefetchRef = useRef(null);
  useEffect(
    () => () => {
      if (lateRefetchRef.current) clearTimeout(lateRefetchRef.current);
    },
    []
  );

  // After an upload: show the new licence and refresh the banner status and the organization's
  // licensed modules without a page reload. Other replicas pick the licence up on their next
  // refresh, so the immediate re-fetch may land on one still holding the old state: re-fetch once
  // more after LICENCE_LATE_REFETCH_MS.
  const handleLicenceUploaded = async (data) => {
    if (data) setLicenceInfo(data);
    if (lateRefetchRef.current) clearTimeout(lateRefetchRef.current);
    lateRefetchRef.current = setTimeout(() => {
      lateRefetchRef.current = null;
      Promise.allSettled([refetchFlags(), refetchLicenses()]);
    }, LICENCE_LATE_REFETCH_MS);
    await Promise.allSettled([refetchFlags(), refetchLicenses()]);
  };

  // When a licence is required, only products in it (incl. derived helpers) can be granted, up to
  // the licence's per-organization allowance. effectiveProducts is null with no licence installed.
  const licenceRequired = licenceInfo?.required === true;
  const effectiveAllowances = new Map(
    (licenceInfo?.effectiveProducts || []).map((p) => [p.product, p.allowance])
  );
  const isProductInLicence = (product) => !licenceRequired || effectiveAllowances.has(product);
  const allowanceFor = (product) =>
    licenceRequired && effectiveAllowances.has(product) ? effectiveAllowances.get(product) : undefined;

  const openModal = (org) => {
    setSelectedOrg(org);
    setModalLicenses(
      (org.licenses || []).map((l) => ({
        ...l,
        _dirty: false,
        _deleted: false,
      }))
    );
    setAddProductValue("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedOrg(null);
    setModalLicenses([]);
  };

  const openDeleteConfirm = (org) => {
    setDeleteConfirmOrg(org);
    setDeleteStep(1);
    setDeleteConfirmText("");
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setDeleteConfirmOrg(null);
    setDeleteStep(1);
    setDeleteConfirmText("");
  };

  const handleNumAssessmentsChange = (index, value) => {
    setModalLicenses((prev) =>
      prev.map((l, i) =>
        i === index ? { ...l, numAssessments: parseInt(value) || 0, _dirty: true } : l
      )
    );
  };

  const handleDeleteLicense = (index) => {
    setModalLicenses((prev) =>
      prev.map((l, i) => (i === index ? { ...l, _deleted: true } : l))
    );
  };

  const handleAddLicense = () => {
    if (!addProductValue) return;
    // Check if already exists (and not deleted)
    const exists = modalLicenses.some(
      (l) => l.licensedProductId === addProductValue && !l._deleted
    );
    if (exists) return;

    setModalLicenses((prev) => [
      ...prev,
      {
        organizationLicensedProductId: null,
        licensedProductId: addProductValue,
        numAssessments: 999,
        _dirty: false,
        _deleted: false,
        _new: true,
      },
    ]);
    setAddProductValue("");
  };

  const handleSave = async () => {
    if (!selectedOrg) return;
    setSaving(true);
    setError(null);

    try {
      const orgId = selectedOrg.organizationId;

      // Process deletes
      for (const license of modalLicenses) {
        if (license._deleted && license.organizationLicensedProductId) {
          await apiCall(
            "DELETE",
            `/SuperAdmin/organizations/${orgId}/licenses/${license.organizationLicensedProductId}`
          );
        }
      }

      // Process creates
      for (const license of modalLicenses) {
        if (license._new && !license._deleted) {
          await apiCall("POST", `/SuperAdmin/organizations/${orgId}/licenses`, {
            licensedProductId: license.licensedProductId,
            numAssessments: license.numAssessments,
          });
        }
      }

      // Process updates
      for (const license of modalLicenses) {
        if (
          license._dirty &&
          !license._new &&
          !license._deleted &&
          license.organizationLicensedProductId
        ) {
          await apiCall(
            "PUT",
            `/SuperAdmin/organizations/${orgId}/licenses/${license.organizationLicensedProductId}`,
            { numAssessments: license.numAssessments }
          );
        }
      }

      // Refresh data
      await loadOrganizations();

      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      const res = await apiCall("POST", `/SuperAdmin/organizations/${org.organizationId}/approve`, null);
      await loadOrganizations();
      if (res?.ownerInviteUrl) {
        setOwnerLink({ id: org.organizationId, url: res.ownerInviteUrl });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to approve organization");
    } finally {
      setActioningOrgId(null);
    }
  };

  const handleReject = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall("POST", `/SuperAdmin/organizations/${org.organizationId}/reject`, null);
      await loadOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject organization");
    } finally {
      setActioningOrgId(null);
    }
  };

  // Org-level on/off for custom remediation workflows (super admin only). Writes only the org
  // flag; the effective state for the org is the global Features:RemediationWorkflows AND this.
  // Other users in the org pick the change up on their next full reload of the app.
  const handleToggleWorkflows = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall(
        "PUT",
        `/SuperAdmin/organizations/${org.organizationId}/remediation-workflows`,
        { enabled: !org.remediationWorkflowsEnabled }
      );
      await loadOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update remediation workflows");
    } finally {
      setActioningOrgId(null);
    }
  };

  // Org-level on/off for the Remediation Scorecard page (super admin only; there is no global flag).
  // Refetches the flags so the nav entry and route guard update for the selected organization
  // without waiting for the poll.
  const handleToggleRemediationScorecard = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall(
        "PUT",
        `/SuperAdmin/organizations/${org.organizationId}/remediation-scorecard`,
        { enabled: !org.remediationScorecardEnabled }
      );
      await loadOrganizations();
      await refetchFlags();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update remediation scorecard");
    } finally {
      setActioningOrgId(null);
    }
  };

  // Org-level on/off for the Security Operations (USOC) page (super admin only). Writes only the
  // org flag; the effective state for the org is the global Features:Usoc AND this.
  // Other users in the org pick the change up on their next full reload of the app.
  const handleToggleUsoc = async (org) => {
    setActioningOrgId(org.organizationId);
    setError(null);
    try {
      await apiCall(
        "PUT",
        `/SuperAdmin/organizations/${org.organizationId}/usoc`,
        { enabled: !org.usocEnabled }
      );
      await loadOrganizations();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update USOC");
    } finally {
      setActioningOrgId(null);
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteConfirmOrg) return;
    setDeleting(true);
    setError(null);
    try {
      await apiCall("DELETE", `/SuperAdmin/organizations/${deleteConfirmOrg.organizationId}`);
      setOrganizations((prev) =>
        prev.filter((o) => o.organizationId !== deleteConfirmOrg.organizationId)
      );
      setDeleteConfirmOrg(null);
      setDeleteStep(1);
      setDeleteConfirmText("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete organization");
      setDeleteConfirmOrg(null);
      setDeleteStep(1);
      setDeleteConfirmText("");
    } finally {
      setDeleting(false);
    }
  };

  if (!isSuperAdmin()) return null;
  if (loading) return <LoadingSpinner />;

  // Available products to add (not already in non-deleted list)
  const availableProducts = LICENSED_PRODUCTS.filter(
    (p) =>
      !modalLicenses.some(
        (l) => l.licensedProductId === p.value && !l._deleted
      )
  );

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-content-primary">
              Super Admin - License Management
            </h1>
            <p className="text-sm text-content-secondary">
              Manage organization licenses and assessments
            </p>
          </div>
        </div>

        <LicenceCard licence={licenceInfo} onUploaded={handleLicenceUploaded} />

        <EnzoicRelayClients />

        {error && !modalOpen && (
          <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
            {error}
          </div>
        )}

        {/* Organizations Table */}
        <div className="bg-theme-secondary border border-border rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-theme-primary/50">
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Organization
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Org ID
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Status
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Licenses
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Workflows
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  USOC
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Scorecard
                </th>
                <th className="text-left px-6 py-3 text-sm font-medium text-content-secondary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <React.Fragment key={org.organizationId}>
                <tr
                  className="border-b border-border last:border-b-0 hover:bg-theme-hover transition-colors"
                >
                  <td className="px-6 py-4 text-sm text-content-primary font-medium">
                    {org.organizationName}
                  </td>
                  <td className="px-6 py-4 text-sm text-content-secondary">
                    {org.organizationId}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        approvalStatusStyles[org.approvalStatus] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                      title={org.approvalNote || undefined}
                    >
                      {org.approvalStatus || "Approved"}
                    </span>
                    {org.approvalStatus === "Pending" && org.approvalNote && (
                      <p className="mt-1 text-xs text-content-muted max-w-xs">
                        {org.approvalNote}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {(org.licenses || []).length === 0 ? (
                        <span className="text-sm text-content-muted italic">
                          No licenses
                        </span>
                      ) : (
                        (org.licenses || []).map((license) => (
                          <span
                            key={license.organizationLicensedProductId}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              productColors[license.licensedProductId] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {license.licensedProductId} ({license.numAssessments})
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          org.remediationWorkflowsEnabled
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {org.remediationWorkflowsEnabled ? "On" : "Off"}
                      </span>
                      <button
                        onClick={() => handleToggleWorkflows(org)}
                        disabled={actioningOrgId === org.organizationId}
                        className="px-2.5 py-1 text-xs text-accent-cyan border border-accent-cyan/40 rounded-lg hover:bg-accent-cyan/10 transition-all disabled:opacity-50"
                        aria-label={`Turn remediation workflows ${
                          org.remediationWorkflowsEnabled ? "off" : "on"
                        } for ${org.organizationName}`}
                        title="Remediation workflows for this organization (also needs the global feature flag)"
                      >
                        {org.remediationWorkflowsEnabled ? "Turn off" : "Turn on"}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          org.usocEnabled
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {org.usocEnabled ? "On" : "Off"}
                      </span>
                      <button
                        onClick={() => handleToggleUsoc(org)}
                        disabled={actioningOrgId === org.organizationId}
                        className="px-2.5 py-1 text-xs text-accent-cyan border border-accent-cyan/40 rounded-lg hover:bg-accent-cyan/10 transition-all disabled:opacity-50"
                        aria-label={`Turn USOC ${
                          org.usocEnabled ? "off" : "on"
                        } for ${org.organizationName}`}
                        title="Security Operations (USOC) for this organization (also needs the global feature flag)"
                      >
                        {org.usocEnabled ? "Turn off" : "Turn on"}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          org.remediationScorecardEnabled
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {org.remediationScorecardEnabled ? "On" : "Off"}
                      </span>
                      <button
                        onClick={() => handleToggleRemediationScorecard(org)}
                        disabled={actioningOrgId === org.organizationId}
                        className="px-2.5 py-1 text-xs text-accent-cyan border border-accent-cyan/40 rounded-lg hover:bg-accent-cyan/10 transition-all disabled:opacity-50"
                        aria-label={`Turn remediation scorecard ${
                          org.remediationScorecardEnabled ? "off" : "on"
                        } for ${org.organizationName}`}
                        title="Remediation Scorecard for this organization"
                      >
                        {org.remediationScorecardEnabled ? "Turn off" : "Turn on"}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {(org.approvalStatus === "Pending" ||
                        org.approvalStatus === "Rejected") && (
                        <button
                          onClick={() => handleApprove(org)}
                          disabled={actioningOrgId === org.organizationId}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                          title="Approve and send the invite email"
                        >
                          <Check className="h-3.5 w-3.5" />
                          Approve
                        </button>
                      )}
                      {org.approvalStatus === "Pending" && (
                        <button
                          onClick={() => handleReject(org)}
                          disabled={actioningOrgId === org.organizationId}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs text-risk-critical border border-risk-critical/40 rounded-lg hover:bg-risk-critical/10 transition-all disabled:opacity-50"
                          title="Reject (no email sent)"
                        >
                          <X className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      )}
                      <button
                        onClick={() => openModal(org)}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 font-medium"
                      >
                        Manage
                      </button>
                      <button
                        onClick={() => setFortinetOrg(org)}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 font-medium"
                        aria-label={`Fortinet diagnostics for ${org.organizationName}`}
                        title="Fortinet diagnostics"
                      >
                        Fortinet
                      </button>
                      <button
                        onClick={() => setKnowBe4Org(org)}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 font-medium"
                        aria-label={`KnowBe4 diagnostics for ${org.organizationName}`}
                        title="KnowBe4 diagnostics"
                      >
                        KnowBe4
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(org)}
                        className="p-1.5 text-content-muted hover:text-risk-critical transition-colors"
                        aria-label="Delete organization"
                        title="Delete organization"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {ownerLink?.id === org.organizationId && (
                  <tr className="border-b border-border last:border-b-0">
                    <td colSpan={8} className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-content-secondary">
                            Email is off on this install. Send this link to the organization owner; they must sign in with the email address they signed up with.
                          </p>
                          <InviteLinkBox url={ownerLink.url} compact />
                        </div>
                        <button
                          type="button"
                          onClick={() => setOwnerLink(null)}
                          className="p-1 text-content-muted hover:text-content-primary rounded transition-colors"
                          aria-label="Dismiss invite link"
                          title="Dismiss"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {organizations.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-8 text-center text-content-muted"
                  >
                    No organizations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <SampleOrganizations />

        <MsspSignupApprovals />
      </div>

      {/* Delete Organization Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmOrg}
        onClose={closeDeleteConfirm}
        title="Delete Organization"
        size="md"
      >
        {deleteStep === 1 ? (
          <div>
            <p className="text-sm text-content-secondary mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-content-primary">
                {deleteConfirmOrg?.organizationName}
              </span>
              ? This operation cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all"
              >
                Go Back
              </button>
              <button
                onClick={() => setDeleteStep(2)}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-risk-critical rounded-lg hover:bg-risk-critical/90 transition-all disabled:opacity-50"
              >
                Yes
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-content-secondary mb-4">
              Type <span className="font-semibold text-content-primary">Confirm</span> to verify you want to delete this organization. This operation cannot be undone.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type 'Confirm' here"
              className="w-full px-3 py-2 border border-border rounded-md bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-risk-critical/50 mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={closeDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all"
              >
                Go Back
              </button>
              <button
                onClick={handleDeleteOrg}
                disabled={deleteConfirmText !== "Confirm" || deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-risk-critical rounded-lg hover:bg-risk-critical/90 transition-all disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Fortinet diagnostics panel (last scan + trace switch) */}
      {fortinetOrg && (
        <SuperAdminFortinetPanel org={fortinetOrg} onClose={() => setFortinetOrg(null)} />
      )}

      {/* KnowBe4 diagnostics panel (last sync, test connection, detailed logging) */}
      {knowBe4Org && (
        <SuperAdminKnowBe4Panel org={knowBe4Org} onClose={() => setKnowBe4Org(null)} />
      )}

      {/* License Management Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={`Manage Licenses - ${selectedOrg?.organizationName || ""}`}
        size="lg"
      >
        {error && (
          <div className="mb-4 p-3 bg-risk-critical/10 border border-risk-critical/30 rounded-lg text-risk-critical text-sm">
            {error}
          </div>
        )}

        {/* Current Licenses */}
        <div className="space-y-3 mb-6">
          {modalLicenses.filter((l) => !l._deleted).length === 0 ? (
            <p className="text-sm text-content-muted italic">
              No licenses assigned
            </p>
          ) : (
            modalLicenses
              .filter((l) => !l._deleted)
              .map((license, idx) => {
                const realIndex = modalLicenses.indexOf(license);
                return (
                  <div
                    key={license.organizationLicensedProductId || `new-${idx}`}
                    className="flex items-center gap-3 p-3 bg-theme-primary rounded-lg border border-border"
                  >
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium min-w-[100px] justify-center ${
                        productColors[license.licensedProductId] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {LICENSED_PRODUCTS.find(
                        (p) => p.value === license.licensedProductId
                      )?.label || license.licensedProductId}
                    </span>
                    <div className="flex items-center gap-2 flex-1">
                      <label className="text-sm text-content-secondary whitespace-nowrap">
                        Assessments:
                      </label>
                      <input
                        type="number"
                        value={license.numAssessments}
                        onChange={(e) =>
                          handleNumAssessmentsChange(realIndex, e.target.value)
                        }
                        className="w-24 px-2 py-1 rounded-md bg-input-bg border border-border text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
                        min="0"
                        max={allowanceFor(license.licensedProductId)}
                        aria-label={`Assessments for ${license.licensedProductId}`}
                      />
                    </div>
                    <button
                      onClick={() => handleDeleteLicense(realIndex)}
                      className="p-1.5 text-content-muted hover:text-risk-critical transition-colors"
                      title="Remove license"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })
          )}
        </div>

        {/* Add License */}
        {availableProducts.length > 0 && (
          <div className="flex items-center gap-2 mb-6 p-3 bg-theme-primary/50 rounded-lg border border-border/50">
            <select
              value={addProductValue}
              onChange={(e) => setAddProductValue(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg bg-input-bg border border-border text-content-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan/50"
            >
              <option value="">Select a product to add...</option>
              {availableProducts.map((p) => {
                const inLicence = isProductInLicence(p.value);
                return (
                  <option
                    key={p.value}
                    value={p.value}
                    disabled={!inLicence}
                    title={inLicence ? undefined : NOT_IN_LICENCE_HINT}
                  >
                    {inLicence ? p.label : `${p.label} (${NOT_IN_LICENCE_HINT})`}
                  </option>
                );
              })}
            </select>
            <button
              onClick={handleAddLicense}
              disabled={!addProductValue}
              className="flex items-center gap-1 px-3 py-2 bg-gradient-primary text-white text-sm rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
        )}

        {/* Save / Cancel */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-sm text-content-secondary hover:text-content-primary bg-transparent border border-border rounded-lg hover:bg-theme-hover transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-gradient-primary rounded-lg hover:shadow-glow-cyan transition-all disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SuperAdmin;
